import { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  getDoc,
  doc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query, 
  orderBy,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import { useToast } from '../components/ui/Toast';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Input } from '../components/ui/FormField';
import { 
  Search, 
  Phone, 
  Mail, 
  Calendar,
  RefreshCw,
  UserPlus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Download,
  UserCog,
  Wallet,
  Plus,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// The driver app renders the agent name with a plain Flutter Text widget,
// which only breaks a line on a REAL newline character. A single-line input
// can't produce one, and a typed backslash-n or <br> stays literal. The name
// is now entered in a <textarea> (pressing Enter inserts a real newline);
// this helper also converts any typed <br> or literal backslash-n into a real
// newline so it works either way. No driver-app update needed.
const normalizeAgentName = (value) =>
  (value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\n/g, '\n');

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const { user: currentAdmin, isMasterAdmin } = useAuth();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    countryCode: '+962'
  });

  // Deposit form state
  const [depositData, setDepositData] = useState({
    amount: '',
    description: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    filterAgents();
  }, [agents, searchTerm]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const agentsRef = collection(db, 'agents');
      const q = query(agentsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const agentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAgents(agentsData);
      setCurrentPage(1); // Reset to first page when data refreshes
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const filterAgents = () => {
    let filtered = [...agents];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(agent => 
        agent.name?.toLowerCase().includes(search) ||
        agent.email?.toLowerCase().includes(search) ||
        agent.phoneNumber?.toLowerCase().includes(search)
      );
    }

    setFilteredAgents(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAgents = filteredAgents.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Invalid email format');
      return false;
    }
    if (!showEditModal && !formData.password) {
      toast.error('Password is required');
      return false;
    }
    if (!showEditModal && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    return true;
  };

  const createAgent = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoadingAction(true);

      // Check if email already exists in agents collection
      const agentsRef = collection(db, 'agents');
      const emailQuery = query(agentsRef, where('email', '==', formData.email));
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        toast.error('An agent with this email already exists');
        return;
      }

      // Combine country code with phone number
      const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber}`;

      // Call Cloud Function to create agent in Firebase Auth and Firestore
      const createAgentFunction = httpsCallable(functions, 'createAgent');
      const result = await createAgentFunction({
        name: normalizeAgentName(formData.name),
        email: formData.email,
        password: formData.password,
        phoneNumber: fullPhoneNumber,
        createdBy: currentAdmin.uid,
        createdByName: currentAdmin.name,
        createdByType: currentAdmin.type
      });

      await fetchAgents();
      logAdminActivity({
        actor: currentAdmin, action: 'create_agent',
        targetType: 'agent', targetId: null,
        targetLabel: `${formData.name} · ${formData.email}`,
      });
      toast.success('Agent created successfully in Firebase Auth and Firestore');
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error(`Failed to create agent: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const updateAgent = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoadingAction(true);

      // Combine country code with phone number
      const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber}`;

      // If password is provided, update via Cloud Function to change Firebase Auth password
      if (formData.password) {
        const updateAgentFunction = httpsCallable(functions, 'updateAgent');
        await updateAgentFunction({
          agentId: selectedAgent.id,
          name: normalizeAgentName(formData.name),
          email: formData.email,
          password: formData.password,
          phoneNumber: fullPhoneNumber,
          updatedBy: currentAdmin.uid,
          updatedByName: currentAdmin.name,
          updatedByType: currentAdmin.type
        });
      } else {
        // Update only Firestore if no password change
        const agentRef = doc(db, 'agents', selectedAgent.id);
        const updateData = {
          name: normalizeAgentName(formData.name),
          email: formData.email,
          phoneNumber: fullPhoneNumber,
          updatedAt: serverTimestamp(),
          updatedBy: currentAdmin.uid,
          updatedByName: currentAdmin.name,
          updatedByType: currentAdmin.type
        };

        await updateDoc(agentRef, updateData);
      }

      await fetchAgents();
      logAdminActivity({
        actor: currentAdmin, action: 'update_agent',
        targetType: 'agent', targetId: selectedAgent?.id || null,
        targetLabel: `${formData.name} · ${formData.email}`,
      });
      toast.success('Agent updated successfully');
      setShowEditModal(false);
      setSelectedAgent(null);
      resetForm();
    } catch (error) {
      console.error('Error updating agent:', error);
      toast.error(`Failed to update agent: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const banAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to ban agent "${agent.name}"?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const agentRef = doc(db, 'agents', agent.id);
      await updateDoc(agentRef, {
        status: 'banned',
        bannedAt: serverTimestamp(),
        bannedBy: currentAdmin.uid,
        bannedByName: currentAdmin.name,
        bannedByType: currentAdmin.type,
        updatedAt: serverTimestamp()
      });

      await fetchAgents();
      logAdminActivity({
        actor: currentAdmin, action: 'ban_agent',
        targetType: 'agent', targetId: agent.id,
        targetLabel: agent.name,
      });
      toast.success(`Agent "${agent.name}" has been banned`);
    } catch (error) {
      console.error('Error banning agent:', error);
      toast.error(`Failed to ban agent: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const unbanAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to unban agent "${agent.name}"?`)) {
      return;
    }

    try {
      setLoadingAction(true);
      
      const agentRef = doc(db, 'agents', agent.id);
      await updateDoc(agentRef, {
        status: 'active',
        bannedAt: null,
        bannedBy: null,
        bannedByName: null,
        bannedByType: null,
        updatedAt: serverTimestamp(),
        unbannedBy: currentAdmin.uid,
        unbannedByName: currentAdmin.name,
        unbannedByType: currentAdmin.type
      });

      await fetchAgents();
      logAdminActivity({
        actor: currentAdmin, action: 'unban_agent',
        targetType: 'agent', targetId: agent.id,
        targetLabel: agent.name,
      });
      toast.success(`Agent "${agent.name}" has been unbanned`);
    } catch (error) {
      console.error('Error unbanning agent:', error);
      toast.error(`Failed to unban agent: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  // admin_master-only HARD delete of an agent (Firebase Auth + Firestore).
  // Uses the existing `deleteAgent` Cloud Function (admin_master enforced).
  const hardDeleteAgent = async (agent) => {
    if (!isMasterAdmin) return;
    const confirm1 = window.confirm(
      `⚠️ Permanently delete agent "${agent.name}" (${agent.phoneNumber || agent.email})?\n\n` +
      `This removes the agent from Firebase Auth AND Firestore. This cannot be undone.`
    );
    if (!confirm1) return;
    const typed = window.prompt(`Type DELETE to confirm deletion of ${agent.name}:`);
    if (typed !== 'DELETE') return;

    try {
      setLoadingAction(true);
      const deleteAgentFn = httpsCallable(functions, 'deleteAgent');
      await deleteAgentFn({ agentId: agent.id });
      logAdminActivity({
        actor: currentAdmin, action: 'delete_agent',
        targetType: 'agent', targetId: agent.id,
        targetLabel: agent.name,
      });
      toast.success(`Agent "${agent.name}" has been permanently deleted`);
      await fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error(`Failed to delete agent: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  // Deposit to agent wallet
  const openDepositModal = async (agent) => {
    try {
      // Fetch the latest agent data to get current balance
      const agentRef = doc(db, 'agents', agent.id);
      const agentSnapshot = await getDoc(agentRef);
      
      if (agentSnapshot.exists()) {
        const latestAgent = {
          id: agentSnapshot.id,
          ...agentSnapshot.data()
        };
        setSelectedAgent(latestAgent);
      } else {
        setSelectedAgent(agent);
      }
    } catch (error) {
      console.error('Error fetching latest agent balance:', error);
      // Fallback to the agent data we have
      setSelectedAgent(agent);
    }
    setDepositData({ amount: '', description: '' });
    setShowDepositModal(true);
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    
    if (!depositData.amount || parseFloat(depositData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoadingAction(true);
    try {
      const depositAmount = parseFloat(depositData.amount);

      const depositFunction = httpsCallable(functions, 'depositToAgent');
      const result = await depositFunction({
        agentId: selectedAgent.id,
        amount: depositAmount,
        description: depositData.description || 'Deposit by admin',
        depositBy: currentAdmin.uid,
        depositByName: currentAdmin.name,
        depositByType: currentAdmin.type
      });

      if (result.data.success) {
        logAdminActivity({
          actor: currentAdmin, action: 'deposit_to_agent',
          targetType: 'agent', targetId: selectedAgent.id,
          targetLabel: `${selectedAgent.name} · +${depositAmount} JOD`,
        });
        toast.success(`Successfully deposited ${depositData.amount} JOR to ${selectedAgent.name}'s wallet`);
        setShowDepositModal(false);
        setDepositData({ amount: '', description: '' });
        await fetchAgents();
      } else {
        toast.error(result.data.message || 'Failed to deposit');
      }
    } catch (error) {
      console.error('Error depositing to agent:', error);
      toast.error(`Failed to deposit: ${error.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    
    // Extract country code and phone number
    let countryCode = '+213';
    let phoneNumber = agent.phoneNumber || '';
    
    if (phoneNumber.startsWith('+')) {
      // Extract country code (assuming 3-4 digits after +)
      const match = phoneNumber.match(/^(\+\d{1,4})(.*)/);
      if (match) {
        countryCode = match[1];
        phoneNumber = match[2];
      }
    }
    
    setFormData({
      name: agent.name,
      email: agent.email,
      password: '',
      phoneNumber: phoneNumber,
      countryCode: countryCode
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phoneNumber: '',
      countryCode: '+962'
    });
    setShowPassword(false);
  };

  const exportToCSV = () => {
    if (filteredAgents.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Name', 'Email', 'Phone Number', 'Wallet Balance', 'Status', 'Created At', 'Created By'];
    const rows = filteredAgents.map(agent => [
      agent.name || '',
      agent.email || '',
      agent.phoneNumber || '',
      (agent.wallet_balance || 0).toFixed(2),
      agent.status || 'active',
      agent.createdAt ? new Date(agent.createdAt.seconds * 1000).toLocaleString() : '',
      getCreatedByText(agent)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `agents_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Agents exported successfully');
  };

  const getCreatedByText = (agent) => {
    if (!agent.createdByType) return agent.createdByName || 'N/A';
    
    if (agent.createdByType === 'admin_master') {
      return 'Master Admin';
    } else {
      return `${agent.createdByName || 'Admin'}${agent.createdByEmail ? ` (${agent.createdByEmail})` : ''}`;
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'banned') {
      return <Badge variant="danger">Banned</Badge>;
    }
    return <Badge variant="success">Active</Badge>;
  };

  // Country codes for phone number dropdown - COMPREHENSIVE LIST
  const countryCodes = [
    { code: '+962', name: 'Jordan', flag: '🇯🇴' },
    { code: '+1', name: 'United States', flag: '🇺🇸' },
    { code: '+7', name: 'Russia', flag: '🇷🇺' },
    { code: '+20', name: 'Egypt', flag: '🇪🇬' },
    { code: '+27', name: 'South Africa', flag: '🇿🇦' },
    { code: '+30', name: 'Greece', flag: '🇬🇷' },
    { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
    { code: '+32', name: 'Belgium', flag: '🇧🇪' },
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+34', name: 'Spain', flag: '🇪🇸' },
    { code: '+36', name: 'Hungary', flag: '🇭🇺' },
    { code: '+39', name: 'Italy', flag: '🇮🇹' },
    { code: '+40', name: 'Romania', flag: '🇷🇴' },
    { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
    { code: '+43', name: 'Austria', flag: '🇦🇹' },
    { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
    { code: '+45', name: 'Denmark', flag: '🇩🇰' },
    { code: '+46', name: 'Sweden', flag: '🇸🇪' },
    { code: '+47', name: 'Norway', flag: '🇳🇴' },
    { code: '+48', name: 'Poland', flag: '🇵🇱' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+51', name: 'Peru', flag: '🇵🇪' },
    { code: '+52', name: 'Mexico', flag: '🇲🇽' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: '+55', name: 'Brazil', flag: '🇧🇷' },
    { code: '+56', name: 'Chile', flag: '🇨🇱' },
    { code: '+57', name: 'Colombia', flag: '🇨🇴' },
    { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
    { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' },
    { code: '+62', name: 'Indonesia', flag: '🇮🇩' },
    { code: '+63', name: 'Philippines', flag: '🇵🇭' },
    { code: '+64', name: 'New Zealand', flag: '🇳🇿' },
    { code: '+65', name: 'Singapore', flag: '🇸🇬' },
    { code: '+66', name: 'Thailand', flag: '🇹🇭' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+82', name: 'South Korea', flag: '🇰🇷' },
    { code: '+84', name: 'Vietnam', flag: '🇻🇳' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+90', name: 'Turkey', flag: '🇹🇷' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+92', name: 'Pakistan', flag: '🇵🇰' },
    { code: '+93', name: 'Afghanistan', flag: '🇦🇫' },
    { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: '+95', name: 'Myanmar', flag: '🇲🇲' },
    { code: '+98', name: 'Iran', flag: '🇮🇷' },
    { code: '+212', name: 'Morocco', flag: '🇲🇦' },
    { code: '+213', name: 'Algeria', flag: '🇩🇿' },
    { code: '+216', name: 'Tunisia', flag: '🇹🇳' },
    { code: '+218', name: 'Libya', flag: '🇱🇾' },
    { code: '+220', name: 'Gambia', flag: '🇬🇲' },
    { code: '+221', name: 'Senegal', flag: '🇸🇳' },
    { code: '+222', name: 'Mauritania', flag: '🇲🇷' },
    { code: '+223', name: 'Mali', flag: '🇲🇱' },
    { code: '+224', name: 'Guinea', flag: '🇬🇳' },
    { code: '+225', name: 'Ivory Coast', flag: '🇨🇮' },
    { code: '+226', name: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+227', name: 'Niger', flag: '🇳🇪' },
    { code: '+228', name: 'Togo', flag: '🇹🇬' },
    { code: '+229', name: 'Benin', flag: '🇧🇯' },
    { code: '+230', name: 'Mauritius', flag: '🇲🇺' },
    { code: '+231', name: 'Liberia', flag: '🇱🇷' },
    { code: '+232', name: 'Sierra Leone', flag: '🇸🇱' },
    { code: '+233', name: 'Ghana', flag: '🇬🇭' },
    { code: '+234', name: 'Nigeria', flag: '🇳🇬' },
    { code: '+235', name: 'Chad', flag: '🇹🇩' },
    { code: '+236', name: 'Central African Republic', flag: '🇨🇫' },
    { code: '+237', name: 'Cameroon', flag: '🇨🇲' },
    { code: '+238', name: 'Cape Verde', flag: '🇨🇻' },
    { code: '+239', name: 'São Tomé and Príncipe', flag: '🇸🇹' },
    { code: '+240', name: 'Equatorial Guinea', flag: '🇬🇶' },
    { code: '+241', name: 'Gabon', flag: '🇬🇦' },
    { code: '+242', name: 'Republic of the Congo', flag: '🇨🇬' },
    { code: '+243', name: 'Democratic Republic of the Congo', flag: '🇨🇩' },
    { code: '+244', name: 'Angola', flag: '🇦🇴' },
    { code: '+245', name: 'Guinea-Bissau', flag: '🇬🇼' },
    { code: '+246', name: 'Diego Garcia', flag: '🇮🇴' },
    { code: '+248', name: 'Seychelles', flag: '🇸🇨' },
    { code: '+249', name: 'Sudan', flag: '🇸🇩' },
    { code: '+250', name: 'Rwanda', flag: '🇷🇼' },
    { code: '+251', name: 'Ethiopia', flag: '🇪🇹' },
    { code: '+252', name: 'Somalia', flag: '🇸🇴' },
    { code: '+253', name: 'Djibouti', flag: '🇩🇯' },
    { code: '+254', name: 'Kenya', flag: '🇰🇪' },
    { code: '+255', name: 'Tanzania', flag: '🇹🇿' },
    { code: '+256', name: 'Uganda', flag: '🇺🇬' },
    { code: '+257', name: 'Burundi', flag: '🇧🇮' },
    { code: '+258', name: 'Mozambique', flag: '🇲🇿' },
    { code: '+260', name: 'Zambia', flag: '🇿🇲' },
    { code: '+261', name: 'Madagascar', flag: '🇲🇬' },
    { code: '+262', name: 'Réunion', flag: '🇷🇪' },
    { code: '+263', name: 'Zimbabwe', flag: '🇿🇼' },
    { code: '+264', name: 'Namibia', flag: '🇳🇦' },
    { code: '+265', name: 'Malawi', flag: '🇲🇼' },
    { code: '+266', name: 'Lesotho', flag: '🇱🇸' },
    { code: '+267', name: 'Botswana', flag: '🇧🇼' },
    { code: '+268', name: 'Eswatini', flag: '🇸🇿' },
    { code: '+269', name: 'Comoros', flag: '🇰🇲' },
    { code: '+290', name: 'Saint Helena', flag: '🇸🇭' },
    { code: '+291', name: 'Eritrea', flag: '🇪🇷' },
    { code: '+297', name: 'Aruba', flag: '🇦🇼' },
    { code: '+298', name: 'Faroe Islands', flag: '🇫🇴' },
    { code: '+299', name: 'Greenland', flag: '🇬🇱' },
    { code: '+350', name: 'Gibraltar', flag: '🇬🇮' },
    { code: '+351', name: 'Portugal', flag: '🇵🇹' },
    { code: '+352', name: 'Luxembourg', flag: '🇱🇺' },
    { code: '+353', name: 'Ireland', flag: '🇮🇪' },
    { code: '+354', name: 'Iceland', flag: '🇮🇸' },
    { code: '+355', name: 'Albania', flag: '🇦🇱' },
    { code: '+356', name: 'Malta', flag: '🇲🇹' },
    { code: '+357', name: 'Cyprus', flag: '🇨🇾' },
    { code: '+358', name: 'Finland', flag: '🇫🇮' },
    { code: '+359', name: 'Bulgaria', flag: '🇧🇬' },
    { code: '+370', name: 'Lithuania', flag: '🇱🇹' },
    { code: '+371', name: 'Latvia', flag: '🇱🇻' },
    { code: '+372', name: 'Estonia', flag: '🇪🇪' },
    { code: '+373', name: 'Moldova', flag: '🇲🇩' },
    { code: '+374', name: 'Armenia', flag: '🇦🇲' },
    { code: '+375', name: 'Belarus', flag: '🇧🇾' },
    { code: '+376', name: 'Andorra', flag: '🇦🇩' },
    { code: '+377', name: 'Monaco', flag: '🇲🇨' },
    { code: '+378', name: 'San Marino', flag: '🇸🇲' },
    { code: '+380', name: 'Ukraine', flag: '🇺🇦' },
    { code: '+381', name: 'Serbia', flag: '🇷🇸' },
    { code: '+382', name: 'Montenegro', flag: '🇲🇪' },
    { code: '+383', name: 'Kosovo', flag: '🇽🇰' },
    { code: '+385', name: 'Croatia', flag: '🇭🇷' },
    { code: '+386', name: 'Slovenia', flag: '🇸🇮' },
    { code: '+387', name: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { code: '+389', name: 'North Macedonia', flag: '🇲🇰' },
    { code: '+420', name: 'Czech Republic', flag: '🇨🇿' },
    { code: '+421', name: 'Slovakia', flag: '🇸🇰' },
    { code: '+423', name: 'Liechtenstein', flag: '🇱🇮' },
    { code: '+500', name: 'Falkland Islands', flag: '🇫🇰' },
    { code: '+501', name: 'Belize', flag: '🇧🇿' },
    { code: '+502', name: 'Guatemala', flag: '🇬🇹' },
    { code: '+503', name: 'El Salvador', flag: '🇸🇻' },
    { code: '+504', name: 'Honduras', flag: '🇭🇳' },
    { code: '+505', name: 'Nicaragua', flag: '🇳🇮' },
    { code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
    { code: '+507', name: 'Panama', flag: '🇵🇦' },
    { code: '+508', name: 'Saint Pierre and Miquelon', flag: '🇵🇲' },
    { code: '+509', name: 'Haiti', flag: '🇭🇹' },
    { code: '+590', name: 'Guadeloupe', flag: '🇬🇵' },
    { code: '+591', name: 'Bolivia', flag: '🇧🇴' },
    { code: '+592', name: 'Guyana', flag: '🇬🇾' },
    { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
    { code: '+594', name: 'French Guiana', flag: '🇬🇫' },
    { code: '+595', name: 'Paraguay', flag: '🇵🇾' },
    { code: '+596', name: 'Martinique', flag: '🇲🇶' },
    { code: '+597', name: 'Suriname', flag: '🇸🇷' },
    { code: '+598', name: 'Uruguay', flag: '🇺🇾' },
    { code: '+599', name: 'Curaçao', flag: '🇨🇼' },
    { code: '+670', name: 'East Timor', flag: '🇹🇱' },
    { code: '+672', name: 'Norfolk Island', flag: '🇳🇫' },
    { code: '+673', name: 'Brunei', flag: '🇧🇳' },
    { code: '+674', name: 'Nauru', flag: '🇳🇷' },
    { code: '+675', name: 'Papua New Guinea', flag: '🇵🇬' },
    { code: '+676', name: 'Tonga', flag: '🇹🇴' },
    { code: '+677', name: 'Solomon Islands', flag: '🇸🇧' },
    { code: '+678', name: 'Vanuatu', flag: '🇻🇺' },
    { code: '+679', name: 'Fiji', flag: '🇫🇯' },
    { code: '+680', name: 'Palau', flag: '🇵🇼' },
    { code: '+681', name: 'Wallis and Futuna', flag: '🇼🇫' },
    { code: '+682', name: 'Cook Islands', flag: '🇨🇰' },
    { code: '+683', name: 'Niue', flag: '🇳🇺' },
    { code: '+684', name: 'American Samoa', flag: '🇦🇸' },
    { code: '+685', name: 'Samoa', flag: '🇼🇸' },
    { code: '+686', name: 'Kiribati', flag: '🇰🇮' },
    { code: '+687', name: 'New Caledonia', flag: '🇳🇨' },
    { code: '+688', name: 'Tuvalu', flag: '🇹🇻' },
    { code: '+689', name: 'French Polynesia', flag: '🇵🇫' },
    { code: '+690', name: 'Tokelau', flag: '🇹🇰' },
    { code: '+691', name: 'Micronesia', flag: '🇫🇲' },
    { code: '+692', name: 'Marshall Islands', flag: '🇲🇭' },
    { code: '+850', name: 'North Korea', flag: '🇰🇵' },
    { code: '+852', name: 'Hong Kong', flag: '🇭🇰' },
    { code: '+853', name: 'Macau', flag: '🇲🇴' },
    { code: '+855', name: 'Cambodia', flag: '🇰🇭' },
    { code: '+856', name: 'Laos', flag: '🇱🇦' },
    { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
    { code: '+886', name: 'Taiwan', flag: '🇹🇼' },
    { code: '+960', name: 'Maldives', flag: '🇲🇻' },
    { code: '+961', name: 'Lebanon', flag: '🇱🇧' },
    { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+967', name: 'Yemen', flag: '🇾🇪' },
    { code: '+968', name: 'Oman', flag: '🇴🇲' },
    { code: '+970', name: 'Palestine', flag: '🇵🇸' },
    { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: '+972', name: 'Israel', flag: '🇮🇱' },
    { code: '+973', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+974', name: 'Qatar', flag: '🇶🇦' },
    { code: '+975', name: 'Bhutan', flag: '🇧🇹' },
    { code: '+976', name: 'Mongolia', flag: '🇲🇳' },
    { code: '+977', name: 'Nepal', flag: '🇳🇵' },
    { code: '+992', name: 'Tajikistan', flag: '🇹🇯' },
    { code: '+993', name: 'Turkmenistan', flag: '🇹🇲' },
    { code: '+994', name: 'Azerbaijan', flag: '🇦🇿' },
    { code: '+995', name: 'Georgia', flag: '🇬🇪' },
    { code: '+996', name: 'Kyrgyzstan', flag: '🇰🇬' },
    { code: '+998', name: 'Uzbekistan', flag: '🇺🇿' },
  ];

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Top-up Agents</h1>
          <p className="text-gray-600 mt-1">Manage wallet top-up agents</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchAgents}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={exportToCSV}
            disabled={filteredAgents.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Agent
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-purple-500">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Agents</p>
                <p className="text-2xl font-bold text-gray-900">{agents.length}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <UserCog className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Active Agents</p>
                <p className="text-2xl font-bold text-green-600">
                  {agents.filter(a => a.status !== 'banned').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Banned Agents</p>
                <p className="text-2xl font-bold text-red-600">
                  {agents.filter(a => a.status === 'banned').length}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <UserX className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Created Today</p>
                <p className="text-2xl font-bold text-blue-600">
                  {agents.filter(a => {
                    if (!a.createdAt) return false;
                    const date = new Date(a.createdAt.seconds * 1000);
                    const today = new Date();
                    return date.toDateString() === today.toDateString();
                  }).length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardBody>
      </Card>

      {/* Agents Table */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold">
            Agents ({filteredAgents.length})
          </h2>
        </CardHeader>
        <CardBody className="p-0">
          {filteredAgents.length === 0 ? (
            <div className="text-center py-12">
              <UserCog className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No agents found</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setShowCreateModal(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create First Agent
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-purple-50 to-blue-50 border-b-2 border-purple-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Wallet Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentAgents.map((agent) => (
                      <tr key={agent.id} className="hover:bg-purple-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {agent.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-4 w-4 mr-2 text-purple-500" />
                            {agent.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="h-4 w-4 mr-2 text-blue-500" />
                            {agent.phoneNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm font-semibold text-green-600">
                            <Wallet className="h-4 w-4 mr-2" />
                            {(agent.wallet_balance || 0).toFixed(2)} JOR
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(agent.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2 text-green-500" />
                            {formatDate(agent.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {getCreatedByText(agent)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(agent)}
                              disabled={loadingAction}
                              title="Edit Agent"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {agent.status === 'banned' ? (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => unbanAgent(agent)}
                                disabled={loadingAction}
                                title="Unban Agent"
                              >
                                Unban
                              </Button>
                            ) : (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => banAgent(agent)}
                                disabled={loadingAction}
                                title="Ban Agent"
                              >
                                Ban
                              </Button>
                            )}
                            {isMasterAdmin && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => hardDeleteAgent(agent)}
                                disabled={loadingAction}
                                title="Permanently delete from Auth + Firestore"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => openDepositModal(agent)}
                              disabled={loadingAction || agent.status === 'banned'}
                              title="Deposit to Wallet"
                            >
                              <Wallet className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredAgents.length > 0 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredAgents.length)} of {filteredAgents.length} agents
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => goToPage(page)}
                              className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                                currentPage === page
                                  ? 'bg-primary-600 text-white'
                                  : 'text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span key={page} className="text-slate-400 px-2">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Create New Agent</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <form onSubmit={createAgent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <textarea
                  className="input-modern"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={"Agent name\n(press Enter for a new line)"}
                  rows={2}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Press Enter to add a line break — it shows exactly the same on every device in the driver app.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="agent@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ minWidth: '140px' }}
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/[^\d]/g, '') })}
                    placeholder="XXXXXXXXX"
                    required
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={loadingAction}
                >
                  {loadingAction ? 'Creating...' : 'Create Agent'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Edit Agent</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedAgent(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <form onSubmit={updateAgent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <textarea
                  className="input-modern"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={"Agent name\n(press Enter for a new line)"}
                  rows={2}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Press Enter to add a line break — it shows exactly the same on every device in the driver app.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="agent@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (leave empty to keep current)
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="New password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ minWidth: '140px' }}
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/[^\d]/g, '') })}
                    placeholder="XXXXXXXXX"
                    required
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Full number: {formData.countryCode}{formData.phoneNumber}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedAgent(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={loadingAction}
                >
                  {loadingAction ? 'Updating...' : 'Update Agent'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Wallet className="h-6 w-6" />
                Deposit to Agent Wallet
              </h2>
              <p className="text-purple-100 text-sm mt-1">
                Deposit funds to {selectedAgent?.name}'s wallet
              </p>
            </div>

            <form onSubmit={handleDeposit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Balance
                </label>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">
                    {(selectedAgent?.wallet_balance || 0).toFixed(2)} JOR
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deposit Amount (JOR) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={depositData.amount}
                  onChange={(e) => setDepositData({ ...depositData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter amount"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={depositData.description}
                  onChange={(e) => setDepositData({ ...depositData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter deposit description"
                  rows="3"
                />
              </div>

              {depositData.amount && parseFloat(depositData.amount) > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    New Balance: <span className="font-bold">
                      {((selectedAgent?.wallet_balance || 0) + parseFloat(depositData.amount)).toFixed(2)} JOR
                    </span>
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDepositModal(false);
                    setDepositData({ amount: '', description: '' });
                  }}
                  disabled={loadingAction}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={loadingAction || !depositData.amount || parseFloat(depositData.amount) <= 0}
                >
                  {loadingAction ? 'Processing...' : 'Deposit'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;